/**
 * roseAltriView.js — Schermata Rose degli altri (copia Rosa + chip B).
 * DOM proprio (#screen-rose-altri); calcoli via rosaCalc su roster da uscite.
 */

import { getRoleChipClass } from '../features/filters/index.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { playerToKey } from '../utils/playerKey.js';
import { MANTRA_ROLES, resolveStudioPlayerMantraRoles } from './studioCalc.js';
import {
  MANTRA_MODULE_IDS,
  MANTRA_MODULE_LINES,
  MANTRA_MODULES,
} from './mantraModules.js';
import {
  assignModuleSlots,
  computeModuleCoverage,
  computeRosaSummary,
  primaryMantraRole,
} from './rosaCalc.js';
import {
  buildModuleRoleTargets,
  buildRoleCoverageRows,
  countOwnedByRole,
} from './rosaModuleTargets.js';
import {
  ROSE_ALTRI_UNASSIGNED_ID,
  buildOpponentRosterFromExits,
  resolveRoseAltriSelectedOpponentId,
} from './roseAltriRoster.js';
import { EXIT_MODE_ASSIGNED } from '../services/db/auctions.pocketbase.js';
import { buildOpponentSpendRows } from './opponentSpend.js';

export const roseAltriViewMethods = {
  renderRoseAltriView() {
    this.state.roseAltriSelectedOpponentId = resolveRoseAltriSelectedOpponentId(
      this.state.roseAltriSelectedOpponentId,
      this.state.auctionOpponents,
      { auctionExits: this.state.auctionExits },
    );
    this._renderRoseAltriOpponentsCompare();
    this._renderRoseAltriStrip();
    this._renderRoseAltriListByRole();
    this._renderRoseAltriPitch();
    this._renderRoseAltriModules();
    this._bindRoseAltriModulesModal();
  },

  _getRoseAltriRosterMap() {
    return buildOpponentRosterFromExits(
      this.state.auctionExits,
      this.state.roseAltriSelectedOpponentId || ROSE_ALTRI_UNASSIGNED_ID,
    );
  },

  _renderRoseAltriOpponentsCompare() {
    const el = document.getElementById('rosa-opponents-compare');
    if (!el) return;
    const assigned = this.state.rosaExitMode === EXIT_MODE_ASSIGNED;
    const opponents = Array.isArray(this.state.auctionOpponents) ? this.state.auctionOpponents : [];
    if (!assigned || !opponents.length) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    const selected = this.state.roseAltriSelectedOpponentId || ROSE_ALTRI_UNASSIGNED_ID;
    const rows = buildOpponentSpendRows(
      this.state.auctionExits,
      opponents,
      {
        budget: Number(this.state.rosaBudget) || 0,
        maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
      },
    ).filter((r) => r.owned > 0 || !r.pile);

    el.hidden = false;
    el.innerHTML = `
      <p class="rosa-opponents-compare-title">Tutte le rose</p>
      <ul class="rosa-opponents-compare-list" data-testid="rosa-opponents-compare-list">
        ${rows.map((r) => `
          <li>
            <button type="button" class="rosa-opponent-compare-btn${r.pile ? ' rosa-opponent-compare-btn--pile' : ''}${r.id === selected ? ' is-on' : ''}"
              data-opponent-id="${escapeHtml(r.id)}"
              data-testid="rosa-opponent-compare-${escapeHtml(r.id)}"
              aria-pressed="${r.id === selected ? 'true' : 'false'}">
              <span class="rosa-opponent-compare-name">${escapeHtml(r.name)}</span>
              <span class="rosa-opponent-compare-meta num">${r.spent}M / ${r.budget}M · ${r.owned}/${r.maxPlayers}</span>
            </button>
          </li>
        `).join('')}
      </ul>
    `;

    el.querySelectorAll('[data-opponent-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-opponent-id');
        if (!id || id === this.state.roseAltriSelectedOpponentId) return;
        this.state.roseAltriSelectedOpponentId = id;
        this.renderRoseAltriView();
      });
    });
  },

  _bindRoseAltriModulesModal() {
    const openBtn = document.getElementById('rose-altri-coverage-open-btn');
    const modal = document.getElementById('rose-altri-modules-modal');
    if (!openBtn || !modal) return;

    const close = () => {
      modal.hidden = true;
      modal.classList.remove('is-open');
      if (this._roseAltriModulesEscHandler) {
        document.removeEventListener('keydown', this._roseAltriModulesEscHandler);
        this._roseAltriModulesEscHandler = null;
      }
    };

    const open = () => {
      this._renderRoseAltriModules();
      modal.hidden = false;
      modal.classList.add('is-open');
      if (this._roseAltriModulesEscHandler) {
        document.removeEventListener('keydown', this._roseAltriModulesEscHandler);
      }
      this._roseAltriModulesEscHandler = (event) => {
        if (event.key === 'Escape') close();
      };
      document.addEventListener('keydown', this._roseAltriModulesEscHandler);
    };

    openBtn.onclick = open;
    const closeBtn = modal.querySelector('.rosa-modules-modal-close');
    if (closeBtn) closeBtn.onclick = close;
    modal.onclick = (event) => {
      if (event.target === modal) close();
    };
  },

  _renderRoseAltriStrip() {
    const el = document.getElementById('rose-altri-strip');
    if (!el) return;
    const roster = this._getRoseAltriRosterMap();
    const playersByKey = new Map(
      (this.state.allPlayers || []).map((player) => [playerToKey(player), player]),
    );
    const entries = Array.from(roster, ([key, entry]) => ({ key, ...entry }));
    const budget = Number(this.state.rosaBudget) || 0;
    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const summary = computeRosaSummary({
      entries,
      budget,
      roleTargets: this.state.rosaRoleTargets || {},
      maxPlayers,
      playersByKey,
    });
    const spentPercent = budget > 0
      ? Math.min(100, Math.round((summary.spent / budget) * 100))
      : 0;
    const auctionName = this.state.activeAuctionName || 'Asta';

    el.innerHTML = `
      <div class="rosa-budget-block">
        <span class="rosa-strip-label">Residui</span>
        <strong class="rosa-budget-residual num">${summary.residui}M</strong>
        <span class="rosa-budget-meta">SPESO <strong class="num">${summary.spent}M</strong> / TOT <strong class="num">${budget}M</strong></span>
        <div class="rosa-budget-bar" aria-label="Budget speso ${summary.spent} su ${budget}">
          <span class="rosa-budget-fill" style="width: ${spentPercent}%"></span>
        </div>
        <span class="rosa-strip-auction-name">${escapeHtml(auctionName)}</span>
      </div>
      <div class="rosa-strip-stats">
        <div class="rosa-strip-stat"><span>ROSA</span><strong class="num">${summary.owned}/${summary.slots}</strong></div>
        <div class="rosa-strip-stat"><span>MEDIA</span><strong class="num">${summary.media}M</strong></div>
        <div class="rosa-strip-stat"><span>MAX OFFERTA</span><strong class="num">${Math.max(0, summary.residui)}M</strong></div>
        <div class="rosa-strip-stat rosa-strip-stat--top"><span>TOP</span><strong>${escapeHtml(summary.topAcquistoName)}${summary.topAcquistoPrice ? ` <em class="num">${summary.topAcquistoPrice}M</em>` : ''}</strong></div>
      </div>
      <div class="rosa-strip-actions">
        <button type="button" class="rosa-settings-btn" id="rose-altri-settings-btn"
                data-testid="rose-altri-settings">Impostazioni</button>
        <button type="button" class="rosa-find-btn" id="rose-altri-find-svincolato-btn"
                data-testid="rose-altri-find-svincolato">Trova svincolato</button>
      </div>
    `;

    el.querySelector('#rose-altri-settings-btn')?.addEventListener('click', () => this.openAuctionSettings?.());
    el.querySelector('#rose-altri-find-svincolato-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
  },

  _resolveRoseAltriRosterPlayers() {
    const roster = this._getRoseAltriRosterMap();
    const allPlayers = this.state.allPlayers || [];
    if (!roster || roster.size === 0) return [];

    const byKey = new Map();
    for (const p of allPlayers) {
      const k = playerToKey(p);
      if (k) byKey.set(k, p);
    }

    const players = [];
    for (const key of roster.keys()) {
      const player = byKey.get(key);
      const roles = player ? resolveStudioPlayerMantraRoles(player) : [];
      players.push({
        playerKey: key,
        name: player?.name || key.split('|')[0] || key,
        roles,
      });
    }
    return players;
  },

  _renderRoseAltriPitch() {
    const el = document.getElementById('rose-altri-pitch');
    if (!el) return;

    const roster = this._getRoseAltriRosterMap();
    const savedModule = this.state.roseAltriSelectedModule;
    const moduleId = MANTRA_MODULES[savedModule] ? savedModule : '4-3-3';
    const assignments = assignModuleSlots(this._resolveRoseAltriRosterPlayers(), moduleId);
    const lines = MANTRA_MODULE_LINES[moduleId];
    let offset = 0;
    const groupedLines = lines.map((count) => {
      const slots = assignments.slice(offset, offset + count);
      offset += count;
      return slots;
    }).reverse();
    const lineRoles = groupedLines.map((_, index) => {
      if (index === 0) return 'att';
      if (index === groupedLines.length - 1) return 'por';
      if (index === groupedLines.length - 2) return 'dif';
      return 'cen';
    });

    const renderSlot = (slot, lineRole) => {
      const preferred = slot.preferredRole;
      const highlightRole = slot.deployedRole || preferred;
      const roleOrder = preferred
        ? [...new Set([highlightRole, preferred, ...(slot.acceptedRoles || [])])]
        : (slot.acceptedRoles || []);
      const rolesHtml = roleOrder.length
        ? `<div class="rosa-pitch-slot-roles roles-container">${roleOrder
            .map((r) => {
              const prefCls = r === highlightRole ? ' role-chip--preferred' : '';
              return `<span class="${getRoleChipClass(r)}${prefCls}">${escapeHtml(r)}</span>`;
            })
            .join('')}</div>`
        : '';
      const isFilled = Boolean(slot.playerKey);
      const price = isFilled ? Number(roster.get(slot.playerKey)?.price) || 0 : 0;
      const circleText = isFilled ? String(price) : '';
      return `
        <div class="rosa-pitch-slot rosa-pitch-slot--${lineRole}${isFilled ? '' : ' rosa-pitch-slot--empty'}"
             data-player-key="${isFilled ? escapeHtml(slot.playerKey) : ''}">
          <span class="rosa-pitch-slot-circle" aria-hidden="true">${escapeHtml(circleText)}</span>
          <span class="rosa-pitch-slot-name" title="${isFilled ? escapeHtml(slot.name) : ''}">${isFilled ? escapeHtml(slot.name) : 'Vuoto'}</span>
          ${rolesHtml}
        </div>
      `;
    };

    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const targets = buildModuleRoleTargets(moduleId, maxPlayers);
    const owned = countOwnedByRole(this._resolveRoseAltriRosterPlayers());
    const rows = buildRoleCoverageRows(owned, targets);
    const coverageHtml = rows.length
      ? `<div class="rosa-module-role-coverage" data-testid="rose-altri-module-role-coverage">
          <div class="rosa-module-role-coverage-title">Copertura rosa · ${escapeHtml(moduleId)}</div>
          <div class="rosa-module-role-coverage-grid">
            ${rows.map((r) => `
              <div class="rosa-role-cov rosa-role-cov--${r.status}" data-testid="rose-altri-role-cov-${escapeHtml(r.role)}">
                <span class="rosa-role-cov-role">${escapeHtml(r.role)}</span>
                <span class="rosa-role-cov-nums num">${r.owned}/${r.target}</span>
              </div>
            `).join('')}
          </div>
        </div>`
      : '';

    el.innerHTML = `
      <div class="rosa-pitch-header">
        <h2 class="rosa-pitch-title">Formazione tipo</h2>
        <label class="rosa-pitch-module-label" for="rose-altri-module-select">
          <span class="visually-hidden">Modulo</span>
          <select id="rose-altri-module-select" class="rosa-pitch-module-select" data-testid="rose-altri-module-select">
            ${MANTRA_MODULE_IDS.map((id) => `<option value="${id}"${id === moduleId ? ' selected' : ''}>${id}</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="rosa-pitch-field" aria-label="Campo con modulo Mantra ${moduleId}">
        <svg class="rosa-pitch-markings" viewBox="0 0 100 100" aria-hidden="true" preserveAspectRatio="none">
          <path d="M0 50H100M0 0H100V100H0ZM50 0V100M50 42A8 8 0 1 0 50 58A8 8 0 1 0 50 42" />
        </svg>
        <div class="rosa-pitch-lines">
          ${groupedLines.map((slots, index) => `
            <div class="rosa-pitch-line rosa-pitch-line--${lineRoles[index]}">
              ${slots.map((slot) => renderSlot(slot, lineRoles[index])).join('')}
            </div>
          `).join('')}
        </div>
      </div>
      ${coverageHtml}
    `;

    el.querySelector('#rose-altri-module-select')?.addEventListener('change', (event) => {
      const nextModule = event.target.value;
      this.state.roseAltriSelectedModule = nextModule;
      try {
        localStorage.setItem('fantapp:v2:roseAltriModule', nextModule);
      } catch {
        /* ignore quota */
      }
      this._renderRoseAltriPitch();
    });
  },

  _renderRoseAltriModules() {
    const el = document.getElementById('rose-altri-modules');
    if (!el) return;

    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const rosterPlayers = this._resolveRoseAltriRosterPlayers();
    const owned = countOwnedByRole(rosterPlayers);
    const modules = computeModuleCoverage(rosterPlayers);

    const cards = modules.map(({ id, filled, total, missing, status }) => {
      const missingText =
        status !== 'complete' && missing.length > 0
          ? `<span class="rosa-module-missing">Manca: ${escapeHtml(missing.join(', '))}</span>`
          : '';
      const roleRows = buildRoleCoverageRows(owned, buildModuleRoleTargets(id, maxPlayers));
      const roleCov = roleRows.length
        ? `<div class="rosa-module-role-cov-mini">${roleRows.map((r) =>
            `<span class="rosa-role-cov rosa-role-cov--${r.status}">${escapeHtml(r.role)} ${r.owned}/${r.target}</span>`
          ).join('')}</div>`
        : '';
      return `
        <div class="rosa-module rosa-module--${escapeHtml(status)}" data-testid="rose-altri-module-${escapeHtml(id)}">
          <span class="rosa-module-id">${escapeHtml(id)}</span>
          <span class="rosa-module-fill">${filled}/${total}</span>
          ${missingText}
          ${roleCov}
        </div>
      `;
    });

    el.innerHTML = `<div class="rosa-modules-grid">${cards.join('')}</div>`;
  },

  _renderRoseAltriListByRole() {
    const el = document.getElementById('rose-altri-list-by-role');
    if (!el) return;
    const roster = this._getRoseAltriRosterMap();
    const selected = this.state.roseAltriSelectedOpponentId || ROSE_ALTRI_UNASSIGNED_ID;
    const isUnassigned = selected === ROSE_ALTRI_UNASSIGNED_ID;

    if (!roster || roster.size === 0) {
      el.innerHTML = `
        <div class="rosa-empty-state" data-testid="rose-altri-empty-state">
          <p class="rosa-empty-title">${isUnassigned ? 'Nessun uscito da assegnare' : 'Nessun giocatore in questa rosa'}</p>
          <p class="rosa-empty-hint">
            ${isUnassigned
    ? 'Gli usciti senza avversario compaiono qui. Assegnali dalle Impostazioni / uscite annotate.'
    : 'Usa <strong>Preso da altri</strong> (con assegnazione) per riempire questa rosa avversaria.'}
          </p>
        </div>
      `;
      return;
    }

    const allPlayers = this.state.allPlayers || [];
    const grouped = new Map(MANTRA_ROLES.map((role) => [role, []]));
    const others = [];

    for (const [key, entry] of roster.entries()) {
      const player = allPlayers.find((p) => playerToKey(p) === key);
      const name = player ? escapeHtml(player.name) : escapeHtml(key.split('|')[0] || key);
      const team = player ? escapeHtml(player.team) : escapeHtml(key.split('|')[1] || '');
      const rawRoles = player
        ? (Array.isArray(player.roles) && player.roles.length > 0
            ? player.roles
            : String(player.role || '').split(';').map((r) => r.trim()).filter(Boolean))
        : [];
      const roleChipsHtml =
        rawRoles.length > 0
          ? `<div class="roles-container">${rawRoles
              .map((r) => `<span class="${getRoleChipClass(r)}">${escapeHtml(r)}</span>`)
              .join('')}</div>`
          : '';
      const price = entry.price || 0;
      const opponentId = entry.opponentId ?? null;
      const item = { key, name, team, rawRoles, roleChipsHtml, price, opponentId };
      const role = primaryMantraRole(rawRoles);
      if (grouped.has(role)) grouped.get(role).push(item);
      else others.push(item);
    }

    const playerChip = ({ key, name, team, roleChipsHtml, price, opponentId }) => `
      <div class="rosa-player-chip" data-player-key="${escapeHtml(key)}">
        <div class="rosa-player-chip-info">
          ${roleChipsHtml}
          <span class="rosa-player-chip-identity">
            <span class="rosa-player-chip-name" title="${name}">${name}</span>
            ${team ? `<span class="rosa-player-chip-team" title="${team}">${team}</span>` : ''}
          </span>
        </div>
        <span class="rosa-player-chip-price num">${price}M</span>
        <div class="rosa-player-chip-actions">
          <button type="button" class="studio-card-btn rosa-edit-price-btn rose-altri-edit-price-btn"
                  data-player-key="${escapeHtml(key)}" data-player-name="${name}" data-player-team="${team}"
                  data-opponent-id="${opponentId == null ? '' : escapeHtml(opponentId)}"
                  aria-label="Modifica prezzo di ${name}">Modifica</button>
          <button type="button" class="studio-card-btn studio-card-btn--icon rose-altri-undo-btn"
                  data-player-key="${escapeHtml(key)}" aria-label="Annulla uscita di ${name}">×</button>
        </div>
      </div>
    `;
    const roleSection = (role, items) => {
      const playersHtml = items.length
        ? `<div class="rosa-role-players">${items.map(playerChip).join('')}</div>`
        : '';

      return `
      <details class="rosa-role-section rosa-role-section--${escapeHtml(role.toLowerCase())}${items.length ? '' : ' rosa-role-section--empty'}">
        <summary class="rosa-role-header">
          <span class="rosa-role-dot ${getRoleChipClass(role)}" aria-hidden="true"></span>
          <span class="rosa-role-title">${escapeHtml(role)}</span>
          <span class="rosa-role-target num">${items.length}</span>
          <span class="rosa-role-chevron" aria-hidden="true"></span>
        </summary>
        <div class="rosa-role-body">${playersHtml}</div>
      </details>
    `;
    };

    const sections = MANTRA_ROLES
      .filter((role) => grouped.get(role).length > 0)
      .map((role) => roleSection(role, grouped.get(role)));
    if (others.length) {
      sections.push(roleSection('Altri', others));
    }
    const ownedTotal = roster.size;
    el.innerHTML = `
  <div class="rosa-list-heading">
    <h2 class="rosa-list-title">Rosa completa</h2>
    <span class="rosa-list-meta">${ownedTotal} GIOCATORI · RUOLI MANTRA</span>
  </div>
  <div class="rosa-role-grid">${sections.join('')}</div>
`;

    el.querySelectorAll('.rose-altri-undo-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.playerKey;
        if (key && confirm('Annullare questa uscita (preso da altri)?')) {
          this.unmarkAuctionExited(key);
        }
      });
    });

    el.querySelectorAll('.rose-altri-edit-price-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.playerKey;
        const playerInfo = { name: btn.dataset.playerName, team: btn.dataset.playerTeam };
        const rawOpp = btn.dataset.opponentId;
        const opponentId = rawOpp === undefined || rawOpp === '' ? null : rawOpp;
        if (key) {
          this.showPresoForm(key, playerInfo, btn, {
            mode: 'others',
            opponentId,
            allowUndo: true,
          });
        }
      });
    });
  },
};
